import { GetIssueResponseDtoStatusEnum } from '@issuemanager/api-client';
import { Chip } from '@issuemanager/apps/client/components';

import {
  useAtlasCoreThemeTokens,
  useAtlasSemanticThemeTokens,
} from '@issuemanager/apps/client/theme';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

type Props = {
  status: GetIssueResponseDtoStatusEnum;
};

export function IssueStatusChip({ status }: Props): React.JSX.Element | null {
  const semantic = useAtlasSemanticThemeTokens();
  const issueStatuses = useIssueStatuses();
  const issueStatus = issueStatuses.find((s) => s.value === status);

  if (!status || !issueStatus) {
    return null;
  }

  return (
    <Chip
      data-testid='issue-status-chip'
      sx={{
        fontWeight: semantic.fontWeight.emphasis.value,
        backgroundColor: issueStatus.backgroundColor,
        color: issueStatus.color,
        borderRadius: 1,
        padding: semantic.spacing.default.value,
        letterSpacing: '1px',
      }}
      label={issueStatus.label.toUpperCase()}
      title={status}
    />
  );
}

export interface IssueStatusData {
  id: string;
  value: GetIssueResponseDtoStatusEnum;
  label: string;
  color: string;
  backgroundColor: string;
  group: string;
}

export const useIssueStatuses = (): IssueStatusData[] => {
  const { t } = useTranslation(['common', 'issue']);
  const semantic = useAtlasSemanticThemeTokens();
  const core = useAtlasCoreThemeTokens();

  const whiteColor = core.color.legacy.white.value;
  const darkColor = semantic.color.text.body.value;

  const issueStatuses: IssueStatusData[] = useMemo(
    () => [
      {
        id: '1',
        value: GetIssueResponseDtoStatusEnum.Open,
        label: t('issue:view.status.open'),
        color: darkColor,
        backgroundColor: semantic.color.status.warning.value,
        group: t('statusLabels.issue'),
      },
      {
        id: '2',
        value: GetIssueResponseDtoStatusEnum.InProgress,
        label: t('issue:view.status.inProgress'),
        color: whiteColor,
        backgroundColor: semantic.color.link.default.value,
        group: t('statusLabels.issue'),
      },
      {
        id: '3',
        value: GetIssueResponseDtoStatusEnum.Closed,
        label: t('issue:view.status.closed'),
        color: whiteColor,
        backgroundColor: semantic.color.status.success.value,
        group: t('statusLabels.issue'),
      },
      {
        id: '4',
        value: GetIssueResponseDtoStatusEnum.Discarded,
        label: t('issue:view.status.discarded'),
        color: whiteColor,
        backgroundColor: semantic.color.text.muted.value,
        group: t('statusLabels.issue'),
      },
      {
        id: '5',
        value: GetIssueResponseDtoStatusEnum.ToBeApproved,
        label: t('issue:view.status.toBeApproved'),
        color: whiteColor,
        backgroundColor: semantic.color.link.default.value,
        group: t('statusLabels.issue'),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [darkColor, semantic, whiteColor],
  );

  return issueStatuses;
};
